import { PDFDocument } from 'pdf-lib';
import { useRememberDocument } from '@/contexts/DocumentWorkspace';
import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { useTranslation } from 'react-i18next';
import { useAiPrefill } from '@/hooks/useAiPrefill';
import { useFileDialog } from '@/hooks/useFileDialog';
import { isDesktop } from '@/lib/fileSystem';
import CloudSourceHint from '@/components/CloudSourceHint';
import {
  Upload,
  ImageIcon,
  FileText,
  Download,
  Save,
  GripVertical,
  X,
  Loader2,
  Check,
  FileSpreadsheet,
  Presentation,
  AlertTriangle,
  Sparkles,
} from 'lucide-react';
import { normalizeAiFormat } from '@/lib/aiAction';
import { pdfjsLib } from '@/lib/pdfWorker';
import { readPdfLibBytes, pdfIntakeErrorMessage } from '@/lib/pdfIntake';
import { imagesToPDF, formatFileSize } from '@/lib/pdfOperations';
import { convertFile as nativeConvert, ocrImage as nativeOCR } from '@/lib/nativebridge';
import { describeConvertFallback } from '@/lib/convertFallback';
import { mergeOcrTextLayers } from '@/lib/ocrDocument';
import { convertDocumentToPDF } from '@/lib/browserConvert';
import { ProBadge } from '@/components/ProBadge';
import PageCanvas from '@/components/PageCanvas';
import JSZip from 'jszip';
import { usageCapApplies, hasReachedLimit, incrementUsage } from '../lib/webUsage';
import FreeTierBanner from '../components/FreeTierBanner';

let imgIdCounter = 0;

// Tesseract reads images, not PDFs: render each scanned page, recognize it to
// a text-only layer, and convert the merged layers (see mergeOcrTextLayers).
const OCR_DPI = 300;
async function recognizeScannedPages(pdfDoc, pageNums, onPage) {
  const layers = [];
  let worker = null;
  try {
    for (const [index, pageNum] of pageNums.entries()) {
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale: OCR_DPI / 72 });
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
      const png = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      let layer = await nativeOCR(await png.arrayBuffer(), 'eng', { localOnly: true, dpi: OCR_DPI });
      if (!layer) {
        if (!worker) {
          const { default: Tesseract } = await import('tesseract.js');
          worker = await Tesseract.createWorker('eng', 1);
          await worker.setParameters({ user_defined_dpi: String(OCR_DPI) });
        }
        const { data } = await worker.recognize(canvas, { pdfTextOnly: true }, { pdf: true });
        if (!data.pdf) throw new Error('The OCR engine did not return a text layer.');
        layer = data.pdf;
      }
      canvas.width = 0;
      canvas.height = 0;
      layers.push(new Uint8Array(layer));
      onPage(index + 1);
    }
  } finally {
    if (worker) await worker.terminate();
  }
  return mergeOcrTextLayers(layers);
}

// ==================== File type detection ====================

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'webp', 'bmp', 'tiff', 'tif', 'heic', 'heif', 'svg'];
const DOC_EXTS = ['doc', 'docx', 'odt', 'rtf', 'txt'];
const SHEET_EXTS = ['xls', 'xlsx', 'csv', 'ods'];
const SLIDE_EXTS = ['ppt', 'pptx', 'odp'];
const WEB_EXTS = ['html', 'htm'];

function getFileCategory(fileName) {
  const ext = fileName.split('.').pop().toLowerCase();
  if (IMAGE_EXTS.includes(ext)) return 'image';
  if (DOC_EXTS.includes(ext)) return 'document';
  if (SHEET_EXTS.includes(ext)) return 'spreadsheet';
  if (SLIDE_EXTS.includes(ext)) return 'presentation';
  if (WEB_EXTS.includes(ext)) return 'web';
  return 'unknown';
}

function getCategoryLabel(category) {
  const labels = {
    document: 'Word Document',
    spreadsheet: 'Spreadsheet',
    presentation: 'Presentation',
    web: 'HTML Document',
  };
  return labels[category] || 'Document';
}

const TO_PDF_ACCEPT = [
  '.jpg', '.jpeg', '.png', '.webp', '.bmp', '.tiff', '.tif', '.heic', '.heif',
  '.doc', '.docx', '.odt', '.rtf', '.txt',
  '.xls', '.xlsx', '.csv', '.ods',
  '.ppt', '.pptx', '.odp',
  '.html', '.htm',
].join(',');

// ==================== To PDF: ?from= deep-link presets ====================
// Drives the initial dropzone copy + file-dialog accept filter when a tool
// card links in with e.g. /convert?tab=to-pdf&from=word. Dropping any
// supported file still works regardless of preset — presentation only.
const TO_PDF_FROM_PRESETS = {
  word:       { accept: '.doc,.docx,.odt,.rtf,.txt',            headKey: 'tools:convert.drop_word',       headFallback: 'Drop a Word document here, or click to browse', hintKey: 'tools:convert.drop_word_hint',       hintFallback: 'DOC, DOCX, ODT, RTF, TXT — converted to PDF' },
  excel:      { accept: '.xls,.xlsx,.csv,.ods',                 headKey: 'tools:convert.drop_excel',      headFallback: 'Drop a spreadsheet here, or click to browse',   hintKey: 'tools:convert.drop_excel_hint',      hintFallback: 'XLS, XLSX, CSV, ODS — converted to PDF' },
  powerpoint: { accept: '.ppt,.pptx,.odp',                      headKey: 'tools:convert.drop_powerpoint', headFallback: 'Drop a presentation here, or click to browse',  hintKey: 'tools:convert.drop_powerpoint_hint', hintFallback: 'PPT, PPTX, ODP — converted to PDF' },
  image:      { accept: '.jpg,.jpeg,.png,.webp,.bmp,.tiff,.tif,.heic,.heif', headKey: 'tools:convert.drop_image', headFallback: 'Drop images here, or click to browse', hintKey: 'tools:convert.drop_image_hint', hintFallback: 'JPG, PNG, WEBP, BMP, TIFF — combined into one PDF' },
};

// ==================== Image → PDF options ====================

const PAGE_SIZE_OPTIONS = [
  { id: 'fit', label: 'Fit to Image' },
  { id: 'a4', label: 'A4' },
  { id: 'letter', label: 'Letter' },
];

const ORIENTATION_OPTIONS = [
  { id: 'auto', label: 'Auto' },
  { id: 'portrait', label: 'Portrait' },
  { id: 'landscape', label: 'Landscape' },
];

const MARGIN_OPTIONS = [
  { id: 'none', label: 'None' },
  { id: 'small', label: 'Small' },
  { id: 'normal', label: 'Normal' },
];

// ==================== PDF → Image options ====================

const FORMAT_OPTIONS = [
  { id: 'png', label: 'PNG' },
  { id: 'jpg', label: 'JPG' },
];

const QUALITY_OPTIONS = [
  { id: 0.6, label: 'Low' },
  { id: 0.8, label: 'Medium' },
  { id: 0.95, label: 'High' },
];

const DPI_OPTIONS = [
  { id: 72, label: '72 DPI' },
  { id: 150, label: '150 DPI' },
  { id: 300, label: '300 DPI' },
];

// ==================== From PDF format cards ====================

const FROM_PDF_FORMATS = [
  { id: 'image', label: 'Images', icon: ImageIcon, sublabel: 'PNG or JPG' },
  { id: 'word', label: 'Word', icon: FileText, sublabel: '.docx' },
  { id: 'excel', label: 'Excel', icon: FileSpreadsheet, sublabel: '.xlsx' },
  { id: 'powerpoint', label: 'PowerPoint', icon: Presentation, sublabel: '.pptx' },
];

const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,image/bmp,image/tiff';

// ==================== Browser-based PDF → Document extraction ====================

/**
 * Extract text from a PDF via PDF.js and build a simple docx/xlsx/pptx.
 * Uses JSZip to create minimal Open XML files (no heavy dependencies).
 */
async function extractPDFToFormat(pdfDoc, pdfPages, selectedPages, outExt, setProgress) {
  // Extract text from selected pages
  const pageTexts = [];
  const pagesToProcess = pdfPages.filter((p) => selectedPages.has(p.id));

  for (let i = 0; i < pagesToProcess.length; i++) {
    const page = await pdfDoc.getPage(pagesToProcess[i].pageNum);
    const content = await page.getTextContent();
    const lines = [];
    let lastY = null;

    for (const item of content.items) {
      if (lastY !== null && Math.abs(item.transform[5] - lastY) > 5) {
        lines.push('\n');
      }
      lines.push(item.str);
      lastY = item.transform[5];
    }

    pageTexts.push(lines.join(' ').replace(/ \n /g, '\n').replace(/\n /g, '\n'));
    setProgress((i + 1) / pagesToProcess.length * 0.8);
  }

  if (outExt === 'docx') return buildDocx(pageTexts);
  if (outExt === 'xlsx') return buildXlsx(pageTexts);
  if (outExt === 'pptx') return buildPptx(pageTexts);
  throw new Error(`Unsupported output format: ${outExt}`);
}

async function buildDocx(pageTexts) {
  const JSZipMod = (await import('jszip')).default || (await import('jszip'));
  const zip = new JSZipMod();

  const paragraphs = pageTexts.flatMap((text, i) => {
    const paras = text.split('\n').filter((l) => l.trim()).map((line) =>
      `<w:p><w:r><w:t xml:space="preserve">${escapeXml(line)}</w:t></w:r></w:p>`
    );
    if (i < pageTexts.length - 1) paras.push('<w:p><w:r><w:br w:type="page"/></w:r></w:p>');
    return paras;
  });

  zip.file('[Content_Types].xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
</Types>`);

  zip.file('_rels/.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`);

  zip.file('word/_rels/document.xml.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"/>`);

  zip.file('word/document.xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:body>${paragraphs.join('\n')}</w:body>
</w:document>`);

  return zip.generateAsync({ type: 'uint8array', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
}

async function buildXlsx(pageTexts) {
  const XLSX = (await import('xlsx')).default || (await import('xlsx'));
  const wb = XLSX.utils.book_new();

  pageTexts.forEach((text, i) => {
    const rows = text.split('\n').filter((l) => l.trim()).map((line) => line.split(/\t|  +/).map((c) => c.trim()));
    const ws = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, `Page ${i + 1}`);
  });

  const bytes = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  return new Uint8Array(bytes);
}

async function buildPptx(pageTexts) {
  const JSZipMod = (await import('jszip')).default || (await import('jszip'));
  const zip = new JSZipMod();

  zip.file('[Content_Types].xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/ppt/presentation.xml" ContentType="application/vnd.openxmlformats-officedocument.presentationml.presentation.main+xml"/>
${pageTexts.map((_, i) => `  <Override PartName="/ppt/slides/slide${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.presentationml.slide+xml"/>`).join('\n')}
</Types>`);

  zip.file('_rels/.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="ppt/presentation.xml"/>
</Relationships>`);

  const slideRels = pageTexts.map((_, i) =>
    `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/slide" Target="slides/slide${i + 1}.xml"/>`
  ).join('\n');

  zip.file('ppt/_rels/presentation.xml.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
${slideRels}
</Relationships>`);

  const slideList = pageTexts.map((_, i) => `<p:sldId id="${256 + i}" r:id="rId${i + 1}"/>`).join('\n');
  zip.file('ppt/presentation.xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<p:presentation xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" xmlns:p="http://schemas.openxmlformats.org/presentationml/2006/main">
  <p:sldIdLst>${slideList}</p:sldIdLst>
</p:presentation>`);

  pageTexts.forEach((text, i) => {
    const paragraphs = text.split('\n').filter((l) => l.trim()).map((line) =>
      `<a:p><a:r><a:rPr lang="en-US" dirty="0"/><a:t>${escapeXml(line)}</a:t></a:r></a:p>`
    ).join('\n');

    zip.file(`ppt/slides/_rels/slide${i + 1}.xml.rels`, `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"/>`);

    zip.file(`ppt/slides/slide${i + 1}.xml`, `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<p:sld xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" xmlns:p="http://schemas.openxmlformats.org/presentationml/2006/main">
  <p:cSld>
    <p:spTree>
      <p:nvGrpSpPr><p:cNvPr id="1" name=""/><p:cNvGrpSpPr/><p:nvPr/></p:nvGrpSpPr>
      <p:grpSpPr/>
      <p:sp>
        <p:nvSpPr><p:cNvPr id="2" name="Content"/><p:cNvSpPr/><p:nvPr/></p:nvSpPr>
        <p:spPr>
          <a:xfrm><a:off x="457200" y="457200"/><a:ext cx="8229600" cy="5486400"/></a:xfrm>
          <a:prstGeom prst="rect"><a:avLst/></a:prstGeom>
        </p:spPr>
        <p:txBody>
          <a:bodyPr/>
          <a:lstStyle/>
          ${paragraphs}
        </p:txBody>
      </p:sp>
    </p:spTree>
  </p:cSld>
</p:sld>`);
  });

  return zip.generateAsync({ type: 'uint8array', mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
}

function escapeXml(str) {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

/**
 * Convert — Full multi-format converter.
 *
 * Tab 1: To PDF — Images, Word, Excel, PPT, HTML → PDF
 * Tab 2: From PDF — PDF → Images, Word, Excel, PPT
 */
export default function Convert() {
  const navigate = useNavigate();
  const rememberDocument = useRememberDocument();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'to-pdf';

  const { openFile, openFiles, saveFile: saveFileDialog } = useFileDialog();
  const location = useLocation();
  const { t } = useTranslation('viewer');

  // ==================== To PDF: Image state ====================
  const [images, setImages] = useState([]);
  const [imgDragging, setImgDragging] = useState(false);
  const [pageSize, setPageSize] = useState('a4');
  const [orientation, setOrientation] = useState('auto');
  const [margin, setMargin] = useState('small');
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // ==================== To PDF: Document state ====================
  const [docFile, setDocFile] = useState(null); // { file, name, size, ext, category }
  const [convertMode, setConvertMode] = useState('basic'); // 'basic' | 'editable' | 'editDirect'
  const [pdfLayout, setPdfLayout] = useState('reading');
  const [bookletPages, setBookletPages] = useState('document');

  // ==================== From PDF state ====================
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pdfFileName, setPdfFileName] = useState('');
  const [pdfArrayBuffer, setPdfArrayBuffer] = useState(null);
  const [pdfDragging, setPdfDragging] = useState(false);
  const [pdfPages, setPdfPages] = useState([]);
  const [selectedPages, setSelectedPages] = useState(new Set());
  const [pdfDragIndex, setPdfDragIndex] = useState(null);
  const [pdfDragOverIndex, setPdfDragOverIndex] = useState(null);
  const [imgFormat, setImgFormat] = useState('png');
  const [imgQuality, setImgQuality] = useState(0.8);
  const [imgDpi, setImgDpi] = useState(150);
  const initialFormat = searchParams.get('format');
  const [outputFormat, setOutputFormat] = useState(
    initialFormat && ['image', 'word', 'excel', 'powerpoint'].includes(initialFormat)
      ? initialFormat
      : 'image'
  ); // 'image' | 'word' | 'excel' | 'powerpoint'
  const [fromPdfMode, setFromPdfMode] = useState('basic'); // 'basic' | 'ocr' | 'editDirect'

  // ==================== To PDF: ?from= deep-link preset (read once at mount) ====================
  const initialFrom = searchParams.get('from');
  const [fromPreset] = useState(
    initialFrom && ['word', 'excel', 'powerpoint', 'image'].includes(initialFrom) ? initialFrom : null
  );

  // ==================== Shared state ====================
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [error, setError] = useState(null);
  const [allowTextFallback, setAllowTextFallback] = useState(false);
  const [result, setResult] = useState(null); // { blob, name, type }
  const [showFreeTierModal, setShowFreeTierModal] = useState(false);

  // ==================== To PDF: Image handlers ====================

  const addFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList);
    if (files.length === 0) return;

    // Categorize all files
    const categories = files.map((f) => getFileCategory(f.name));
    const hasImages = categories.some((c) => c === 'image');
    const hasDocs = categories.some((c) => c !== 'image' && c !== 'unknown');

    // Mixed upload check
    if (hasImages && hasDocs) {
      setError('Please upload either images or documents, not both.');
      return;
    }

    setError(null);
    setResult(null);

    // Document/spreadsheet/presentation/web — single file mode
    if (hasDocs) {
      const file = files[0]; // Take only the first document
      const ext = file.name.split('.').pop().toLowerCase();
      const category = getFileCategory(file.name);
      setDocFile({ file, name: file.name, size: file.size, ext, category });
      setPdfLayout('reading');
      setImages([]); // Clear any images
      return;
    }

    // Images — multi-file mode
    if (hasImages) {
      setDocFile(null); // Clear any doc
      const validFiles = files.filter((f) => getFileCategory(f.name) === 'image');

      const entries = await Promise.all(
        validFiles.map(async (file) => {
          const ext = file.name.split('.').pop().toLowerCase();

          // HEIC/HEIF: graceful error
          if (ext === 'heic' || ext === 'heif') {
            return {
              id: `img-${++imgIdCounter}`,
              name: file.name,
              size: file.size,
              dataUrl: null,
              width: 0,
              height: 0,
              error: 'HEIC format detected. Please convert to JPG first.',
            };
          }

          try {
            const dataUrl = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.readAsDataURL(file);
            });

            const canvasDataUrl = await normalizeImage(dataUrl);
            const dims = await getImageDimensions(canvasDataUrl);
            return {
              id: `img-${++imgIdCounter}`,
              name: file.name,
              size: file.size,
              dataUrl: canvasDataUrl,
              width: dims.width,
              height: dims.height,
            };
          } catch {
            return {
              id: `img-${++imgIdCounter}`,
              name: file.name,
              size: file.size,
              dataUrl: null,
              width: 0,
              height: 0,
              error: 'Could not load image. Try converting to JPG or PNG first.',
            };
          }
        })
      );

      setImages((prev) => [...prev, ...entries]);
    }
  }, []);

  const removeImage = useCallback((id) => {
    setImages((prev) => prev.filter((img) => img.id !== id));
  }, []);

  // Drag reorder
  const handleImgDragStart = useCallback((e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setDragImage(e.currentTarget, 20, 20);
  }, []);

  const handleImgDragEnd = useCallback(() => {
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      setImages((prev) => {
        const next = [...prev];
        const [dragged] = next.splice(dragIndex, 1);
        next.splice(dragOverIndex, 0, dragged);
        return next;
      });
    }
    setDragIndex(null);
    setDragOverIndex(null);
  }, [dragIndex, dragOverIndex]);

  const handleImgDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== dragOverIndex) setDragOverIndex(index);
  }, [dragOverIndex]);

  const displayImages = useMemo(() => {
    if (dragIndex === null || dragOverIndex === null || dragIndex === dragOverIndex) return images;
    const reordered = [...images];
    const [dragged] = reordered.splice(dragIndex, 1);
    reordered.splice(dragOverIndex, 0, dragged);
    return reordered;
  }, [images, dragIndex, dragOverIndex]);

  const handleConvertToPDF = useCallback(async () => {
    const validImages = images.filter((img) => img.dataUrl);
    if (validImages.length === 0) return;

    setProcessing(true);
    setProgress(0);
    setProgressLabel('Converting images…');
    setError(null);

    try {
      const bytes = await imagesToPDF(validImages, { pageSize, orientation, margin }, setProgress);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const name = validImages.length === 1
        ? validImages[0].name.replace(/\.[^.]+$/, '.pdf')
        : 'images_converted.pdf';
      setResult({ blob, name, type: 'pdf' });
    } catch (err) {
      setError(`Conversion failed: ${err.message}`);
    } finally {
      setProcessing(false);
      setProgressLabel('');
    }
  }, [images, pageSize, orientation, margin]);

  const handleBrowseFiles = useCallback(async () => {
    if (docFile || images.length === 0) {
      // Universal file picker — narrowed to the deep-link preset's types when
      // no file has been loaded yet; dropping any supported type still works.
      const accept = images.length === 0 && !docFile
        ? (TO_PDF_FROM_PRESETS[fromPreset]?.accept ?? TO_PDF_ACCEPT)
        : TO_PDF_ACCEPT;
      const results = await openFiles(accept, { conversionMetadata: true });
      if (results.length === 0) return;
      const fileList = results.map((r) => r.buffer === undefined && r.path
        ? { name: r.name, size: r.size, nativePath: r.path }
        : new File([r.buffer], r.name, { type: r.type || 'application/octet-stream' }));
      addFiles(fileList);
    } else {
      // Already have images — add more images
      const results = await openFiles(IMAGE_ACCEPT);
      if (results.length === 0) return;
      const fileList = results.map((r) => new File([r.buffer], r.name, { type: 'image/*' }));
      addFiles(fileList);
    }
  }, [openFiles, addFiles, docFile, images.length, fromPreset]);

  // ==================== To PDF: Document conversion handler ====================

  const handleConvertDocument = useCallback(async () => {
    if (!docFile) return;
    setProcessing(true);
    setProgress(0);
    setError(null);

    try {
      let arrayBuffer = docFile.file.nativePath ? null : await docFile.file.arrayBuffer();
      setProgressLabel('Converting to PDF…');

      // Try native (LibreOffice) first, fall back to browser-based conversion
      let pdfBytes = null;
      let engineUsed = 'layout';
      let fallbackReason = null;
      let conversionError = null;
      try {
        pdfBytes = await nativeConvert(arrayBuffer, '.' + docFile.ext, 'pdf', {
          inputPath: docFile.file.nativePath,
          inputBytes: docFile.size,
          pdfLayout,
          bookletPages: bookletPages === 'document' ? null : Number(bookletPages),
        });
      } catch (err) {
        conversionError = err;
        fallbackReason = describeConvertFallback(err);
      }
      if (!pdfBytes) {
        if (pdfLayout === 'booklet' || !allowTextFallback && !['txt', 'csv'].includes(docFile.ext)) throw conversionError || new Error('Layout conversion is unavailable. Enable text-only conversion below to extract content without the original formatting.');
        engineUsed = 'text';
        setProgressLabel('Extracting document text…');
        if (!arrayBuffer && docFile.file.nativePath) arrayBuffer = await window.electronAPI.readFile(docFile.file.nativePath);
        pdfBytes = await convertDocumentToPDF(arrayBuffer, docFile.ext);
      }

      setProgress(0.8);
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const pdfName = docFile.name.replace(/\.[^.]+$/, pdfLayout === 'booklet' ? '-booklet.pdf' : '.pdf');

      if (engineUsed === 'layout' && (convertMode === 'editDirect' || convertMode === 'editable')) {
        const file = new File([blob], pdfName, { type: 'application/pdf' });
        navigate('/viewer?mode=editText', { state: { file } });
        return;
      }

      // Basic: show result screen
      setResult({ blob, name: pdfName, type: 'pdf', engineUsed, fallbackReason, pdfLayout });
    } catch (err) {
      setError(`Conversion failed: ${err.message}`);
    } finally {
      setProcessing(false);
      setProgressLabel('');
    }
  }, [docFile, convertMode, navigate, allowTextFallback, pdfLayout, bookletPages]);

  // ==================== From PDF: handlers ====================

  let pdfPageIdCounter = useRef(0);

  const loadPDF = useCallback(
    async (file) => {
      if (!file || file.type !== 'application/pdf') return;
      if (pdfDoc) pdfDoc.destroy();

      setError(null);
      setResult(null);
      setPdfFileName(file.name);

      try {
        // Decrypt owner-password PDFs (lossless) / reject truly locked ones
        // before pdf-lib ever sees the bytes — see src/lib/pdfIntake.js.
        const buf = await readPdfLibBytes(file);
        setPdfArrayBuffer(buf.slice(0));
        const doc = await pdfjsLib.getDocument({ data: buf }).promise;
        setPdfDoc(doc);
        rememberDocument(file);

        const pages = Array.from({ length: doc.numPages }, (_, i) => ({
          id: `pp-${++pdfPageIdCounter.current}`,
          pageNum: i + 1,
        }));
        setPdfPages(pages);
        setSelectedPages(new Set(pages.map((p) => p.id)));
      } catch (err) {
        setError(pdfIntakeErrorMessage(err, 'convert it'));
      }
    },
    [pdfDoc, rememberDocument]
  );

  const handleBrowsePDF = useCallback(async () => {
    const result = await openFile('.pdf');
    if (!result) return;
    const file = new File([result.buffer], result.name, { type: 'application/pdf' });
    loadPDF(file);
  }, [openFile, loadPDF]);

  // AI prefill: switch to From-PDF tab, set output format, load file.
  // See useAiPrefill for the ref-guard / no-strip-navigate rationale.
  const { aiPrefilled } = useAiPrefill({
    location,
    loadFn: loadPDF,
    onPrefill: (state) => {
      if (searchParams.get('tab') !== 'from-pdf') {
        setSearchParams({ tab: 'from-pdf' }, { replace: true });
      }
      if (state.aiFormat) setOutputFormat('image');
      const aiFormat = normalizeAiFormat(state.aiFormat);
      if (aiFormat) setImgFormat(aiFormat);
    },
  });

  const togglePage = useCallback((id) => {
    setSelectedPages((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAllPages = useCallback(() => {
    setSelectedPages((prev) =>
      prev.size === pdfPages.length
        ? new Set()
        : new Set(pdfPages.map((p) => p.id))
    );
  }, [pdfPages]);

  const deletePdfPage = useCallback((id) => {
    setPdfPages((prev) => prev.filter((p) => p.id !== id));
    setSelectedPages((prev) => { const n = new Set(prev); n.delete(id); return n; });
  }, []);

  // Reset file when all pages deleted
  useEffect(() => {
    if (pdfDoc && pdfPages.length === 0) {
      pdfDoc.destroy();
      setPdfDoc(null);
      setPdfFileName('');
      setPdfArrayBuffer(null);
      setResult(null);
    }
  }, [pdfPages.length, pdfDoc]);

  // PDF page drag reorder
  const handlePdfDragStart = useCallback((e, index) => {
    setPdfDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setDragImage(e.currentTarget, 30, 30);
  }, []);

  const handlePdfDragEnd = useCallback(() => {
    if (pdfDragIndex !== null && pdfDragOverIndex !== null && pdfDragIndex !== pdfDragOverIndex) {
      setPdfPages((prev) => {
        const next = [...prev];
        const [dragged] = next.splice(pdfDragIndex, 1);
        next.splice(pdfDragOverIndex, 0, dragged);
        return next;
      });
    }
    setPdfDragIndex(null);
    setPdfDragOverIndex(null);
  }, [pdfDragIndex, pdfDragOverIndex]);

  const handlePdfDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== pdfDragOverIndex) setPdfDragOverIndex(index);
  }, [pdfDragOverIndex]);

  const displayPdfPages = useMemo(() => {
    if (pdfDragIndex === null || pdfDragOverIndex === null || pdfDragIndex === pdfDragOverIndex) return pdfPages;
    const reordered = [...pdfPages];
    const [dragged] = reordered.splice(pdfDragIndex, 1);
    reordered.splice(pdfDragOverIndex, 0, dragged);
    return reordered;
  }, [pdfPages, pdfDragIndex, pdfDragOverIndex]);

  const handleConvertToImage = useCallback(async () => {
    if (!pdfDoc || selectedPages.size === 0) return;

    setProcessing(true);
    setProgress(0);
    setError(null);

    const pagesToExport = pdfPages
      .filter((p) => selectedPages.has(p.id))
      .map((p) => p.pageNum);

    const scale = imgDpi / 72;
    const mimeType = imgFormat === 'png' ? 'image/png' : 'image/jpeg';
    const ext = imgFormat === 'png' ? 'png' : 'jpg';
    const baseName = pdfFileName.replace(/\.pdf$/i, '');

    try {
      if (pagesToExport.length === 1) {
        setProgressLabel('Rendering page…');
        const blob = await renderPageToBlob(pdfDoc, pagesToExport[0], scale, mimeType, imgFormat === 'jpg' ? imgQuality : undefined);
        setResult({ blob, name: `${baseName}_page${pagesToExport[0]}.${ext}`, type: 'image' });
      } else {
        setProgressLabel('Rendering pages…');
        const zip = new JSZip();

        for (let i = 0; i < pagesToExport.length; i++) {
          const blob = await renderPageToBlob(pdfDoc, pagesToExport[i], scale, mimeType, imgFormat === 'jpg' ? imgQuality : undefined);
          const arrayBuffer = await blob.arrayBuffer();
          zip.file(`${baseName}_page${pagesToExport[i]}.${ext}`, arrayBuffer);
          setProgress((i + 1) / pagesToExport.length);
        }

        setProgressLabel('Creating ZIP…');
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        setResult({ blob: zipBlob, name: `${baseName}_images.zip`, type: 'zip' });
      }
    } catch (err) {
      setError(`Conversion failed: ${err.message}`);
    } finally {
      setProcessing(false);
      setProgressLabel('');
    }
  }, [pdfDoc, pdfPages, selectedPages, imgDpi, imgFormat, imgQuality, pdfFileName]);

  // ==================== From PDF: Document conversion handler ====================

  const handleConvertFromPDF = useCallback(async () => {
    if (!pdfDoc || !pdfArrayBuffer) return;

    if (fromPdfMode === 'editDirect') {
      const file = new File([pdfArrayBuffer], pdfFileName, { type: 'application/pdf' });
      navigate('/viewer?mode=editText', { state: { file } });
      return;
    }

    setProcessing(true);
    setProgress(0);
    setError(null);

    try {
      const order = pdfPages.filter((p) => selectedPages.has(p.id)).map((p) => p.pageNum - 1);
      if (!order.length) throw new Error('Select at least one page');
      const source = await PDFDocument.load(pdfArrayBuffer);
      const selected = await PDFDocument.create();
      for (const page of await selected.copyPages(source, order)) selected.addPage(page);
      let inputBytes = await selected.save();

      if (fromPdfMode === 'ocr') {
        setProgressLabel('Running OCR…');
        try {
          const pageNums = pdfPages.filter((p) => selectedPages.has(p.id)).map((p) => p.pageNum);
          inputBytes = await recognizeScannedPages(pdfDoc, pageNums, (done) => setProgress(0.5 * done / pageNums.length));
        } catch (err) { throw new Error(`OCR could not complete: ${err.message}`); }
        setProgress(0.5);
      }

      const formatMap = { word: 'docx', excel: 'xlsx', powerpoint: 'pptx' };
      const outExt = formatMap[outputFormat];
      const formatLabel = outputFormat === 'word' ? 'Word' : outputFormat === 'excel' ? 'Excel' : 'PowerPoint';
      setProgressLabel(`Converting to ${formatLabel}…`);

      // Try the layout-preserving engine first (desktop native LibreOffice /
      // server-side pdf2docx). Capture WHY it couldn't run so the text-only
      // fallback below is shown as a labeled downgrade, never a silent one.
      let resultBytes = null;
      let engineUsed = 'layout';
      let fallbackReason = null;
      try {
        resultBytes = await nativeConvert(inputBytes, '.pdf', outExt);
      } catch (err) {
        fallbackReason = describeConvertFallback(err);
      }

      // Fallback: text-only extraction (no layout, tables, or images). This is
      // a real downgrade — flag it on the result so the user isn't handed a
      // bare-text file thinking it's the product's best output.
      if (!resultBytes) {
        if (!allowTextFallback) throw new Error('Layout conversion is unavailable. Enable text-only conversion below to extract content without the original formatting.');
        engineUsed = 'text';
        if (!fallbackReason) fallbackReason = describeConvertFallback(null);
        setProgressLabel(`Extracting text for ${formatLabel}…`);
        const extractedDoc = await pdfjsLib.getDocument({ data: new Uint8Array(inputBytes) }).promise;
        try {
          const orderedPages = Array.from({ length: extractedDoc.numPages }, (_, i) => ({ id: `extract-${i}`, pageNum: i + 1 }));
          resultBytes = await extractPDFToFormat(extractedDoc, orderedPages, new Set(orderedPages.map((p) => p.id)), outExt, setProgress);
        } finally { await extractedDoc.destroy(); }
      }

      setProgress(1);
      const mimeMap = {
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      };

      const blob = new Blob([resultBytes], { type: mimeMap[outExt] });
      const name = pdfFileName.replace(/\.pdf$/i, `.${outExt}`);
      setResult({ blob, name, type: outExt, engineUsed: engineUsed === 'layout' && outExt === 'xlsx' ? 'tables' : engineUsed, fallbackReason });
    } catch (err) {
      setError(`Conversion failed: ${err.message}`);
    } finally {
      setProcessing(false);
      setProgressLabel('');
    }
  }, [pdfDoc, pdfArrayBuffer, pdfFileName, pdfPages, selectedPages, outputFormat, fromPdfMode, navigate, allowTextFallback]);

  // ==================== Shared: result handlers ====================

  const handleDownload = useCallback(async () => {
    if (!result) return;
    if (usageCapApplies() && hasReachedLimit()) {
      setShowFreeTierModal(true);
      return;
    }
    try {
      const saved = await saveFileDialog(result.blob, result.name);
      if (saved && usageCapApplies()) incrementUsage();
    } catch (err) { setError(`Could not save the converted file: ${err.message}`); }
  }, [result, saveFileDialog]);

  const handleOpenInViewer = useCallback(() => {
    if (!result || result.type !== 'pdf') return;
    const file = new File([result.blob], result.name, { type: 'application/pdf' });
    navigate('/viewer', { state: { file } });
  }, [result, navigate]);

  // ==================== Render ====================

  const allPagesSelected = pdfPages.length > 0 && selectedPages.size === pdfPages.length;
  const somePagesSelected = selectedPages.size > 0 && selectedPages.size < pdfPages.length;
  const validImageCount = images.filter((img) => img.dataUrl).length;
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-xl font-bold text-gray-900">Convert</h1>
        </div>
        <div className="max-w-5xl mx-auto flex gap-1 mt-3">
          <TabButton active={activeTab === 'to-pdf'} onClick={() => { setSearchParams({ tab: 'to-pdf' }); setResult(null); setError(null); }} icon={FileText}>{t('tools:convert.tabs.to_pdf')}</TabButton>
          <TabButton active={activeTab === 'from-pdf'} onClick={() => { setSearchParams({ tab: 'from-pdf' }); setResult(null); setError(null); }} icon={ImageIcon}>{t('tools:convert.tabs.from_pdf')}</TabButton>
        </div>
      </div>

      <div className="max-w-5xl mx-auto w-full p-6 flex-1 flex flex-col">
        {!result && (docFile || pdfDoc && outputFormat !== 'image') && <label className="flex items-start gap-2 mb-4 text-sm text-gray-700">
          <input type="checkbox" checked={allowTextFallback} onChange={(event) => setAllowTextFallback(event.target.checked)} className="mt-1" />
          <span>Allow text-only conversion if layout conversion is unavailable<span className="block text-xs text-gray-500">Extracts text and cell values. Original formatting, graphics and page layout may be lost.</span></span>
        </label>}


        {/* ==================== Result screen ==================== */}
        {result && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
            <div className="flex items-center justify-center gap-2 text-green-600 mb-4">
              <Check size={20} />
              <span className="font-medium">Conversion complete</span>
            </div>
            <p className="text-sm text-gray-600 mb-2">{result.name}</p>
            {result.pdfLayout === 'booklet' && <div className="text-left bg-brand-light/30 border border-brand-blue/20 rounded-lg p-4 mb-4 text-sm text-gray-700">
              <p className="font-medium text-gray-900">Ready for booklet printing</p>
              <p className="mt-1">Print at 100% on both sides, flipping on the short edge. Pages are already arranged for folding; leave your printer’s booklet and multiple-pages-per-sheet options off.</p>
              <p className="mt-1">Blank pages are added at the end of a booklet when needed. Print a test sheet first to check your printer’s duplex orientation.</p>
            </div>}
            {result.engineUsed === 'tables' ? <p className="text-xs text-gray-500 mb-6">Tables and text extracted into cells. Review cell alignment and values.</p> : result.engineUsed === 'text' ? (
              <div className="mb-6 text-left bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                <p className="text-sm font-medium text-amber-800">Text-only conversion &mdash; formatting wasn&rsquo;t preserved</p>
                <p className="text-xs text-amber-700 mt-1">{result.fallbackReason}</p>
              </div>
            ) : result.engineUsed === 'layout' ? (
              <p className="text-xs text-gray-400 mb-6">Converted with the layout engine. Review the result before sharing.</p>
            ) : (
              <div className="mb-4" />
            )}
            <div className="flex gap-3 justify-center flex-wrap">
              {result.type === 'pdf' && (
                <button onClick={handleOpenInViewer} className="flex items-center gap-2 px-5 py-2.5 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors">
                  <FileText size={16} />Open in Viewer
                </button>
              )}
              <button onClick={handleDownload} className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors">
                {isDesktop()
                  ? (<><Save size={16} />Save</>)
                  : (<><Download size={16} />Download</>)}
              </button>
            </div>
            <button onClick={() => setResult(null)} className="mt-4 text-sm text-gray-500 hover:text-gray-700 transition-colors">
              Convert another
            </button>
          </div>
        )}

        {/* ==================== To PDF tab ==================== */}
        {activeTab === 'to-pdf' && !result && (
          <>
            {/* Upload zone */}
            <div
              onDrop={(e) => { e.preventDefault(); setImgDragging(false); addFiles(e.dataTransfer.files); }}
              onDragOver={(e) => { e.preventDefault(); setImgDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setImgDragging(false); }}
              onClick={handleBrowseFiles}
              className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all mb-6 ${
                imgDragging ? 'border-brand-blue bg-brand-light/30 scale-[1.01]' : 'border-gray-300 hover:border-brand-blue hover:bg-gray-50'
              }`}
            >
              <Upload size={32} className={`mx-auto mb-2 ${imgDragging ? 'text-brand-blue' : 'text-gray-400'}`} />
              {fromPreset && images.length === 0 && !docFile ? (
                <>
                  <p className="text-base font-medium text-gray-700">
                    {t(TO_PDF_FROM_PRESETS[fromPreset].headKey, TO_PDF_FROM_PRESETS[fromPreset].headFallback)}
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    {t(TO_PDF_FROM_PRESETS[fromPreset].hintKey, TO_PDF_FROM_PRESETS[fromPreset].hintFallback)}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-base font-medium text-gray-700">
                    {images.length > 0 ? 'Add more files' : docFile ? 'Change file' : 'Drop files here, or click to browse'}
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    Images, Word, Excel, PowerPoint, HTML — or any document
                  </p>
                </>
              )}
            </div>

            {/* ── Document single-file mode ── */}
            {docFile && (
              <>
                {/* File info card */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-brand-light/30 flex items-center justify-center shrink-0">
                    <FileText size={20} className="text-brand-blue" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{docFile.name}</p>
                    <p className="text-xs text-gray-500">{getCategoryLabel(docFile.category)} · {formatFileSize(docFile.size)}</p>
                  </div>
                  <button onClick={() => setDocFile(null)} className="text-sm text-brand-blue hover:underline font-medium">Change</button>
                </div>

                {/* Conversion options radio panel */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Convert to PDF</h3>

                  {/* Option 1: Standard */}
                  <label className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer mb-3 transition-colors ${
                    convertMode === 'basic' ? 'border-brand-blue bg-brand-light/30' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input type="radio" name="convertMode" value="basic" checked={convertMode === 'basic'} onChange={() => setConvertMode('basic')}
                      className="mt-0.5 w-4 h-4 text-brand-blue" />
                    <div>
                      <p className="font-medium text-gray-900">Standard</p>
                      <p className="text-sm text-gray-500">Convert to a standard PDF document</p>
                    </div>
                  </label>

                  {/* Option 2: Editable — Pro */}
                  <label className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer mb-3 transition-colors ${
                    convertMode === 'editable' ? 'border-brand-blue bg-brand-light/30' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input type="radio" name="convertMode" value="editable" checked={convertMode === 'editable'} onChange={() => setConvertMode('editable')}
                      className="mt-0.5 w-4 h-4 text-brand-blue" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">Editable</p>
                        <ProBadge />
                      </div>
                      <p className="text-sm text-gray-500">Convert and detect all text for in-place editing</p>
                    </div>
                  </label>

                  {/* Divider */}
                  <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs text-gray-400">or</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>

                  {/* Option 3: Open in Editor — Pro */}
                  <label className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                    convertMode === 'editDirect' ? 'border-brand-blue bg-brand-light/30' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input type="radio" name="convertMode" value="editDirect" checked={convertMode === 'editDirect'} onChange={() => setConvertMode('editDirect')}
                      className="mt-0.5 w-4 h-4 text-brand-blue" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">Open in Editor</p>
                        <ProBadge />
                      </div>
                      <p className="text-sm text-gray-500">Convert to PDF and open directly in the text editor</p>
                    </div>
                  </label>

                  {isDesktop() && docFile.category === 'document' && <fieldset disabled={processing} className="mt-5 border-t border-gray-200 pt-4">
                    <legend className="text-sm font-semibold text-gray-900 pt-4">PDF page layout</legend>
                    <label className="flex items-start gap-3 mt-2 cursor-pointer">
                      <input type="radio" name="pdfLayout" checked={pdfLayout === 'reading'} onChange={() => setPdfLayout('reading')} className="mt-1" />
                      <span><span className="font-medium text-sm">Reading pages</span><span className="block text-xs text-gray-500">One page at a time, in reading order. Word DOCX Book Fold page sizes are preserved.</span></span>
                    </label>
                    <label className="flex items-start gap-3 mt-3 cursor-pointer">
                      <input type="radio" name="pdfLayout" checked={pdfLayout === 'booklet'} onChange={() => setPdfLayout('booklet')} className="mt-1" />
                      <span><span className="font-medium text-sm">Booklet sheets</span><span className="block text-xs text-gray-500">Two pages per side, ordered for double-sided printing, folding and binding. Uses local processing.</span></span>
                    </label>
                    {pdfLayout === 'booklet' && <label className="block text-sm mt-4">Pages per booklet
                      <select value={bookletPages} onChange={event => setBookletPages(event.target.value)} className="block w-full mt-1 rounded-lg border border-gray-300 p-2">
                        <option value="document">Use Word setting, or all pages</option>
                        <option value="8">8 pages / 2 sheets</option><option value="16">16 pages / 4 sheets</option>
                        <option value="24">24 pages / 6 sheets</option><option value="32">32 pages / 8 sheets</option>
                        <option value="0">All pages in one booklet</option>
                      </select>
                    </label>}
                  </fieldset>}

                  {/* Error */}
                  {error && <p className="text-red-600 text-sm mt-4">{error}</p>}

                  {/* Progress */}
                  <ProgressBar processing={processing} progress={progress} label={progressLabel} />

                  {/* Convert button */}
                  <button onClick={handleConvertDocument} disabled={processing}
                    className="w-full mt-6 flex items-center justify-center gap-2 px-5 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    Convert →
                  </button>
                </div>
              </>
            )}

            {/* ── Image multi-file mode ── */}
            {!docFile && images.length > 0 && (
              <>
                <div className="space-y-2 mb-6">
                  {displayImages.map((img, index) => {
                    const realIndex = images.indexOf(img);
                    const isBeingDragged = realIndex === dragIndex;
                    const isDropTarget = dragIndex !== null && dragOverIndex !== null && index === dragOverIndex && dragIndex !== dragOverIndex;

                    return (
                      <div
                        key={img.id}
                        draggable={!img.error}
                        onDragStart={(e) => handleImgDragStart(e, realIndex)}
                        onDragEnd={handleImgDragEnd}
                        onDragOver={(e) => handleImgDragOver(e, index)}
                        className={`bg-white rounded-xl border-2 p-3 flex items-center gap-3 transition-all duration-150 ${
                          img.error ? 'border-red-200 bg-red-50/30'
                            : isBeingDragged ? 'opacity-25 scale-[0.98] border-gray-200'
                            : isDropTarget ? 'border-brand-blue ring-2 ring-brand-blue/20'
                            : 'border-gray-200 hover:shadow-sm'
                        }`}
                      >
                        <div className="cursor-grab active:cursor-grabbing text-gray-400">
                          <GripVertical size={16} />
                        </div>
                        {img.dataUrl ? (
                          <img src={img.dataUrl} alt={img.name} className="w-12 h-12 object-cover rounded border border-gray-200" />
                        ) : (
                          <div className="w-12 h-12 rounded border border-gray-200 bg-gray-100 flex items-center justify-center">
                            <AlertTriangle size={16} className="text-amber-500" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{img.name}</p>
                          {img.error ? (
                            <p className="text-xs text-red-500">{img.error}</p>
                          ) : (
                            <p className="text-xs text-gray-500">{img.width} × {img.height} · {formatFileSize(img.size)}</p>
                          )}
                        </div>
                        <button onClick={() => removeImage(img.id)} className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors">
                          <X size={16} />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Options */}
                <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
                  <div className="grid grid-cols-3 gap-4">
                    <OptionGroup label="Page Size" options={PAGE_SIZE_OPTIONS} value={pageSize} onChange={setPageSize} />
                    <OptionGroup label="Orientation" options={ORIENTATION_OPTIONS} value={orientation} onChange={setOrientation} disabled={pageSize === 'fit'} />
                    <OptionGroup label="Margin" options={MARGIN_OPTIONS} value={margin} onChange={setMargin} disabled={pageSize === 'fit'} />
                  </div>
                </div>

                {/* Convert button */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
                  <ProgressBar processing={processing} progress={progress} label={progressLabel} />
                  <button onClick={handleConvertToPDF} disabled={processing || validImageCount === 0} className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    <FileText size={18} />
                    Convert {validImageCount} Image{validImageCount !== 1 ? 's' : ''} to PDF
                  </button>
                </div>
              </>
            )}

            {/* Empty state */}
            {!docFile && images.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <ImageIcon size={48} className="mx-auto mb-4 opacity-50" />
                <p>Add images or documents to convert them to PDF</p>
              </div>
            )}
          </>
        )}

        {/* ==================== From PDF tab ==================== */}
        {activeTab === 'from-pdf' && !result && (
          <>
            {/* Upload */}
            {!pdfDoc ? (
              <>
                <div
                  onDrop={(e) => { e.preventDefault(); setPdfDragging(false); loadPDF(e.dataTransfer.files[0]); }}
                  onDragOver={(e) => { e.preventDefault(); setPdfDragging(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setPdfDragging(false); }}
                  onClick={handleBrowsePDF}
                  className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all mb-6 ${
                    pdfDragging ? 'border-brand-blue bg-brand-light/30 scale-[1.01]' : 'border-gray-300 hover:border-brand-blue hover:bg-gray-50'
                  }`}
                >
                  <Upload size={32} className={`mx-auto mb-2 ${pdfDragging ? 'text-brand-blue' : 'text-gray-400'}`} />
                  <p className="text-base font-medium text-gray-700">Drop a PDF here, or click to browse</p>
                  <p className="text-sm text-gray-400 mt-1">Convert to images, Word, Excel, or PowerPoint</p>
                </div>
                <CloudSourceHint
                  onPicked={(r) => loadPDF(new File([r.buffer], r.name, { type: 'application/pdf' }))}
                  className="mb-6"
                />
                {error && <p role="alert" className="text-red-600 text-sm -mt-3 mb-6">{error}</p>}
              </>
            ) : (
              <>
                {/* File info */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex items-center gap-3">
                  <div className="w-8 h-11 rounded border border-gray-200 overflow-hidden shrink-0 bg-white">
                    <PageCanvas pdfDoc={pdfDoc} pageNumber={1} scale={0.08} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{pdfFileName}</p>
                    <p className="text-xs text-gray-500">{pdfPages.length} page{pdfPages.length !== 1 ? 's' : ''}</p>
                  </div>
                  <button onClick={handleBrowsePDF} className="text-sm text-brand-blue hover:underline font-medium">Change file</button>
                </div>

                {/* Output format selector */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  {FROM_PDF_FORMATS.map((fmt) => (
                    <button
                      key={fmt.id}
                      onClick={() => setOutputFormat(fmt.id)}
                      className={`p-4 rounded-xl border-2 text-center transition-all ${
                        outputFormat === fmt.id
                          ? 'border-brand-blue bg-brand-light/30'
                          : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                      }`}
                    >
                      <fmt.icon size={24} className={outputFormat === fmt.id ? 'text-brand-blue mx-auto mb-1' : 'text-gray-400 mx-auto mb-1'} />
                      <p className="font-medium text-sm text-gray-900">{fmt.label}</p>
                      <p className="text-xs text-gray-400">{fmt.sublabel}</p>
                    </button>
                  ))}
                </div>

                {/* ── Image output: existing page grid + DPI/format options ── */}
                <>
                    {/* Select pages */}
                    <div className="flex items-center gap-3 mb-4">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={allPagesSelected}
                          ref={(el) => { if (el) el.indeterminate = somePagesSelected; }}
                          onChange={toggleAllPages}
                          className="w-4 h-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue"
                        />
                        <span className="text-sm text-gray-700">Select all</span>
                      </label>
                      <span className="text-sm text-gray-500">{selectedPages.size} of {pdfPages.length} selected</span>
                    </div>

                    {/* Page grid */}
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 mb-6">
                      {displayPdfPages.map((page, index) => {
                        const realIndex = pdfPages.indexOf(page);
                        const isSelected = selectedPages.has(page.id);
                        const isBeingDragged = realIndex === pdfDragIndex;
                        const isDropTarget = pdfDragIndex !== null && pdfDragOverIndex !== null && index === pdfDragOverIndex && pdfDragIndex !== pdfDragOverIndex;

                        return (
                          <div
                            key={page.id}
                            draggable
                            onDragStart={(e) => handlePdfDragStart(e, realIndex)}
                            onDragEnd={handlePdfDragEnd}
                            onDragOver={(e) => handlePdfDragOver(e, index)}
                            onClick={() => togglePage(page.id)}
                            className={`relative rounded-xl overflow-hidden border-2 cursor-pointer transition-all duration-150 group ${
                              isBeingDragged
                                ? 'opacity-25 scale-95 border-gray-200'
                                : isDropTarget
                                  ? 'border-brand-blue ring-2 ring-brand-blue/20 scale-[1.02]'
                                  : isSelected
                                    ? 'border-brand-blue shadow-sm'
                                    : 'border-gray-200 opacity-50 hover:opacity-80 hover:border-gray-300'
                            }`}
                          >
                            <div className="absolute top-1 left-1 z-10 p-0.5 rounded text-gray-400 opacity-0 group-hover:opacity-70 transition-opacity cursor-grab active:cursor-grabbing">
                              <GripVertical size={12} />
                            </div>
                            <div className="absolute top-1.5 left-5 z-10" onClick={(e) => e.stopPropagation()}>
                              <input aria-label={`Select page ${page.pageNum}`} type="checkbox" checked={isSelected} onChange={() => togglePage(page.id)}
                                className="w-4 h-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue cursor-pointer" />
                            </div>
                            <button
                              aria-label={`Remove page ${page.pageNum} from conversion`}
                              onClick={(e) => { e.stopPropagation(); deletePdfPage(page.id); }}
                              className="absolute top-1 right-1 z-10 p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                            >
                              <X size={14} />
                            </button>
                            <PageCanvas pdfDoc={pdfDoc} pageNumber={page.pageNum} scale={0.18} />
                            <div className={`absolute bottom-0 inset-x-0 text-center py-0.5 text-xs font-medium ${
                              isSelected ? 'bg-brand-blue text-white' : 'bg-gray-800/60 text-white'
                            }`}>
                              {page.pageNum}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {outputFormat === 'image' && (<>
                    {/* Image options */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
                      <div className="grid grid-cols-3 gap-4">
                        <OptionGroup label="Format" options={FORMAT_OPTIONS} value={imgFormat} onChange={setImgFormat} />
                        <OptionGroup label="Quality" options={QUALITY_OPTIONS} value={imgQuality} onChange={setImgQuality} disabled={imgFormat === 'png'} />
                        <OptionGroup label="Resolution" options={DPI_OPTIONS} value={imgDpi} onChange={setImgDpi} />
                      </div>
                    </div>

                    {/* AI prefill banner */}
                    {aiPrefilled && !processing && !result && (
                      <div className="mb-4 flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
                        <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>{t('ai.banner_convert')}</span>
                      </div>
                    )}

                    {/* Convert button */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
                      <ProgressBar processing={processing} progress={progress} label={progressLabel} />
                      <button onClick={handleConvertToImage} disabled={processing || selectedPages.size === 0} className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        <ImageIcon size={18} />
                        Convert {selectedPages.size} Page{selectedPages.size !== 1 ? 's' : ''} to {imgFormat.toUpperCase()}
                      </button>
                    </div>
                  </>
                )}

                </>
                {/* ── Document output: radio options panel ── */}
                {outputFormat !== 'image' && (
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-base font-semibold text-gray-900 mb-4">
                      Convert to {outputFormat === 'word' ? 'Word' : outputFormat === 'excel' ? 'Excel' : 'PowerPoint'}
                    </h3>

                    {/* Option 1: Standard */}
                    <label className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer mb-3 transition-colors ${
                      fromPdfMode === 'basic' ? 'border-brand-blue bg-brand-light/30' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input type="radio" name="fromPdfMode" value="basic" checked={fromPdfMode === 'basic'} onChange={() => setFromPdfMode('basic')}
                        className="mt-0.5 w-4 h-4 text-brand-blue" />
                      <div>
                        <p className="font-medium text-gray-900">Standard</p>
                        <p className="text-sm text-gray-500">Keeps layout, tables and images when possible. Complex or scanned PDFs convert best with Advanced (OCR).</p>
                      </div>
                    </label>

                    {/* Option 2: Advanced with OCR — Pro */}
                    <label className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer mb-3 transition-colors ${
                      fromPdfMode === 'ocr' ? 'border-brand-blue bg-brand-light/30' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input type="radio" name="fromPdfMode" value="ocr" checked={fromPdfMode === 'ocr'} onChange={() => setFromPdfMode('ocr')}
                        className="mt-0.5 w-4 h-4 text-brand-blue" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">Advanced with OCR</p>
                          <ProBadge />
                        </div>
                        <p className="text-sm text-gray-500">Recognize text in images and scans for full editability</p>
                      </div>
                    </label>

                    {/* Divider */}
                    <div className="flex items-center gap-3 my-4">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-xs text-gray-400">or</span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>

                    {/* Option 3: Edit PDF directly — Pro */}
                    <label className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                      fromPdfMode === 'editDirect' ? 'border-brand-blue bg-brand-light/30' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input type="radio" name="fromPdfMode" value="editDirect" checked={fromPdfMode === 'editDirect'} onChange={() => setFromPdfMode('editDirect')}
                        className="mt-0.5 w-4 h-4 text-brand-blue" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">Edit PDF directly</p>
                          <ProBadge />
                        </div>
                        <p className="text-sm text-gray-500">Skip converting — edit text right in the PDF</p>
                      </div>
                    </label>

                    {/* Error */}
                    {error && <p className="text-red-600 text-sm mt-4">{error}</p>}

                    {/* Progress */}
                    <ProgressBar processing={processing} progress={progress} label={progressLabel} />

                    <button onClick={handleConvertFromPDF} disabled={processing}
                      className="w-full mt-6 flex items-center justify-center gap-2 px-5 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      {fromPdfMode === 'editDirect' ? 'Open in Editor →' : 'Convert →'}
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      <FreeTierBanner open={showFreeTierModal} onClose={() => setShowFreeTierModal(false)} />
    </div>
  );
}

// ==================== Sub-components ====================

function TabButton({ active, onClick, icon: Icon, children }) {
  return (
    <button onClick={onClick} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
      active ? 'bg-brand-blue text-white' : 'text-gray-600 hover:bg-gray-100'
    }`}>
      <Icon size={16} />{children}
    </button>
  );
}

function OptionGroup({ label, options, value, onChange, disabled }) {
  return (
    <div className={disabled ? 'opacity-40 pointer-events-none' : ''}>
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{label}</label>
      <div className="flex flex-col gap-1">
        {options.map((opt) => (
          <button
            key={opt.id}
            onClick={() => onChange(opt.id)}
            className={`px-3 py-1.5 text-sm rounded-lg text-left transition-colors ${
              value === opt.id ? 'bg-brand-blue text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ProgressBar({ processing, progress, label }) {
  if (!processing) return null;
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Loader2 size={16} className="animate-spin text-brand-blue" />
        <span className="text-sm text-gray-600">{label || `${Math.round(progress * 100)}%`}</span>
      </div>
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-brand-blue rounded-full transition-all duration-200" style={{ width: `${progress * 100}%` }} />
      </div>
    </div>
  );
}

// ==================== Helpers ====================

async function getImageDimensions(dataUrl) {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.src = dataUrl;
  });
}

/**
 * Normalize image to PNG data URL via canvas.
 * Handles WEBP, BMP, TIFF, etc. by drawing to canvas and re-exporting.
 * JPEG stays as JPEG; everything else becomes PNG.
 */
async function normalizeImage(dataUrl) {
  const isJpeg = dataUrl.includes('image/jpeg') || dataUrl.includes('image/jpg');
  if (isJpeg || dataUrl.includes('image/png')) return dataUrl;

  const img = new window.Image();
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = dataUrl;
  });

  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  canvas.getContext('2d').drawImage(img, 0, 0);
  return canvas.toDataURL('image/png');
}

/**
 * Render a PDF page to an image blob.
 * Does NOT multiply by devicePixelRatio — we want exact DPI for export.
 */
async function renderPageToBlob(pdfDoc, pageNum, scale, mimeType, quality) {
  const page = await pdfDoc.getPage(pageNum);
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext('2d');

  await page.render({ canvasContext: ctx, viewport }).promise;

  return new Promise((resolve) =>
    canvas.toBlob(resolve, mimeType, quality)
  );
}
